// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserSearch, Clock, Check, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface ScoutRequest {
  id: string;
  scout_id: string;
  athlete_id: string;
  message?: string;
  requested_at: string;
  status: string;
  scout: {
    id: string;
    name?: string;
    username?: string;
    avatar_url?: string;
  } | null;
}

const ScoutRequestsAlert: React.FC = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<ScoutRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchScoutRequests = async () => {
      if (!profile?.id) {
        setIsLoading(false);
        return;
      }

      try {
        console.log('🔍 Fetching scout requests for athlete:', profile.id);
        
        const { data, error } = await supabase
          .from('scout_athlete_requests')
          .select(`
            id,
            scout_id,
            athlete_id,
            message,
            requested_at,
            status,
            scout:profiles!scout_id (
              id,
              name,
              username,
              avatar_url
            )
          `)
          .eq('athlete_id', profile.id)
          .eq('status', 'pending')
          .order('requested_at', { ascending: false });

        if (error) {
          console.error('❌ Error fetching scout requests:', error);
        } else {
          console.log('✅ Scout requests fetched:', data);
          // Handle the case where scout might be an array from the join
          const formattedData = (data || []).map(request => ({
            ...request,
            scout: Array.isArray(request.scout) ? request.scout[0] : request.scout
          }));
          setRequests(formattedData);
        }
      } catch (err) {
        console.error('❌ Error in fetchScoutRequests:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScoutRequests();
  }, [profile?.id]);

  const handleRequestAction = async (requestId: string, action: 'approved' | 'rejected') => {
    if (!profile?.id) return;

    setProcessingIds(prev => new Set([...prev, requestId]));

    try {
      const request = requests.find(r => r.id === requestId);
      if (!request) return;

      console.log(`🔄 ${action === 'approved' ? 'Approving' : 'Rejecting'} scout request:`, {
        requestId,
        scoutId: request.scout_id,
        athleteId: request.athlete_id
      });

      // If approved, create the scout-athlete relationship first
      if (action === 'approved') {
        console.log('📝 Creating scout-athlete relationship...');
        
        // Check if relationship already exists to avoid duplicates
        const { data: existingRelationship, error: checkError } = await supabase
          .from('scout_athlete_relationships')
          .select('id')
          .eq('scout_id', request.scout_id)
          .eq('athlete_id', request.athlete_id)
          .maybeSingle();

        if (checkError) {
          console.error('❌ Error checking existing relationship:', checkError);
          throw checkError;
        }

        if (!existingRelationship) {
          const { error: relationshipError } = await supabase
            .from('scout_athlete_relationships')
            .insert([{
              scout_id: request.scout_id,
              athlete_id: request.athlete_id,
              membership_type: 'view_only',
              status: 'active',
              start_date: new Date().toISOString().split('T')[0] // YYYY-MM-DD
            }]);

          if (relationshipError) {
            console.error('❌ Error creating scout relationship:', relationshipError);
            throw relationshipError;
          }

          console.log('✅ Scout-athlete relationship created successfully');
        } else {
          console.log('ℹ️ Scout-athlete relationship already exists');
        }
      }

      // Update the request status
      const { error: updateError } = await supabase
        .from('scout_athlete_requests')
        .update({ status: action })
        .eq('id', requestId);

      if (updateError) {
        console.error('❌ Error updating request status:', updateError);
        throw updateError;
      }

      // Remove from local state
      setRequests(prev => prev.filter(r => r.id !== requestId));

      toast({
        title: action === 'approved' ? '✅ Scout Connection Approved' : 'Request Rejected',
        description: action === 'approved' 
          ? 'The scout now has view-only access to your profile.' 
          : 'The scout request has been rejected.',
      });

    } catch (error) {
      console.error(`❌ Error ${action === 'approved' ? 'approving' : 'rejecting'} request:`, error);
      toast({
        title: 'Action Failed',
        description: `Failed to ${action === 'approved' ? 'approve' : 'reject'} the request. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  if (isLoading) {
    return null; // Don't show loading state for this component
  }

  if (requests.length === 0) {
    return null; // Don't show anything if no requests
  }

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-blue-800">
          <UserSearch className="h-5 w-5" />
          Scout Access Requests
          <Badge variant="secondary" className="ml-auto">
            {requests.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {requests.map((request) => {
          const isProcessing = processingIds.has(request.id);
          const scoutName = request.scout?.name || request.scout?.username || 'Unknown Scout';
          
          return (
            <div key={request.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border">
              <Avatar className="h-10 w-10">
                <AvatarImage src={request.scout?.avatar_url} alt={scoutName} />
                <AvatarFallback className="bg-blue-100 text-blue-600">
                  {scoutName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-gray-900">{scoutName}</h4>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="h-3 w-3" />
                    {new Date(request.requested_at).toLocaleDateString()}
                  </div>
                </div>
                
                {request.message && (
                  <p className="text-sm text-gray-600 mt-1">{request.message}</p>
                )}
                
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={() => handleRequestAction(request.id, 'approved')}
                    disabled={isProcessing}
                    className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-1"
                  >
                    <Check className="h-3 w-3" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRequestAction(request.id, 'rejected')}
                    disabled={isProcessing}
                    className="border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-1"
                  >
                    <X className="h-3 w-3" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default ScoutRequestsAlert;